<template>
  <div class="iam-list-table" v-loading="loading">
    <el-table
      ref="table"
      row-key="id"
      v-if="rows && rows.length > 0"
      :data="rows"
      @sort-change="doChangeSort"
      @row-click="onRowClick"
    >

      <!-- Name -->
      <el-table-column
        :resizable="false"
        :label="fields.fullName.label"
        :prop="fields.fullName.label"
      >
        <template slot-scope="scope">
          <div
            v-if="scope.row.avatars.length > 0"
            class="list__avatar"
            :style="{ backgroundImage: getIamAvatar(scope.row) }"
          />
          <span
            v-else
            v-text="'-'"
          />
          <el-tooltip
            :disabled="!hasPermissionToAuditLogs"
            effect="dark"
            :content="scope.row.id"
            placement="bottom"
          >
            <span
              class="list__avatar__name"
              v-text="scope.row.fullName ? presenter(scope.row, 'fullName') : presenter(scope.row, 'firstName')"
            />
          </el-tooltip>
        </template>
      </el-table-column>

      <!-- Email -->
      <el-table-column :resizable="false" :label="fields.email.label" :prop="fields.email.name">
        <template slot-scope="scope">{{ presenter(scope.row, 'email') }}</template>
      </el-table-column>

      <!-- Status -->
      <el-table-column
        :label="fields.disabledAsStatus.label"
        :prop="fields.disabledAsStatus.name"
        :resizable="false"
        :width="165"
      >
        <template slot-scope="scope">
          <el-tag :type="scope.row[fields.disabledAsStatus.name] ? 'danger' : 'success'">
            <span
              class="el-tag__ellipsis"
              v-text="presenter(scope.row, 'disabledAsStatus')"
            />
          </el-tag>
        </template>
      </el-table-column>

      <!-- TODO: show roles on function manage -->
      <!-- <el-table-column 
      v-if="!currentUser.isAdmin"
      :label="fields.roles.label" :prop="fields.roles.name">
        <template slot-scope="scope">
          <div :key="roleId" v-for="roleId in scope.row.roles">
            <el-tooltip :content="roleDescriptionOf(roleId)">
              <span>{{ roleLabelOf(roleId) }}</span>
            </el-tooltip>
          </div>
        </template>
      </el-table-column> -->

      <!-- Created At -->
      <el-table-column
        :label="fields.createdAt.label"
        :prop="fields.createdAt.name"
        :resizable="false"
        :width="150"
      >
        <template slot-scope="scope">{{ presenter(scope.row, 'createdAt') }}</template>
      </el-table-column>

      <!-- Last Login -->
      <el-table-column
        :label="fields.lastLogin.label"
        :prop="fields.lastLogin.name"
        :resizable="false"
        :width="170"
        sortable="custom"
      >
        <template slot-scope="scope">
          <span
            v-if="scope.row.lastLogin"
            v-text="presenter(scope.row, 'lastLogin')"
          />
          <app-i18n
            v-else
            code="common.never"
          />
        </template>
      </el-table-column>

      <el-table-column :resizable="false" :fixed="isMobile? undefined : 'right'" align="center" width="70">
        <template slot-scope="scope">
          <div class="table-actions">
            <router-link :to="`/iam/managers/${scope.row.id}/edit`" v-if="hasPermissionToEdit">
              <el-button
                icon="icon-pen"
                circle
              />
            </router-link>
          </div>
        </template>
      </el-table-column>
    </el-table>

    <app-empty-page
      v-else
      :icon-class="'icon-family'"
      :empty-text="i18n('iam.managers.empty')"
    />

    <div class="el-pagination-wrapper" v-if="rows && rows.length > 0">
      <el-pagination
        :current-page="pagination.currentPage || 1"
        :disabled="loading"
        :layout="paginationLayout"
        :total="count"
        @current-change="doChangePaginationCurrentPage"
        @size-change="doChangePaginationPageSize"
      ></el-pagination>
    </div>
  </div>
</template>

<script>
import { AuditLogPermissions } from '@modules/audit-log/audit-log-permissions';
import { UserModel } from '@modules/auth/user-model';
import { mapGetters, mapActions } from 'vuex';
import { IamPermissions } from '@modules/iam/iam-permissions';
import Roles from '@security/roles';
import { i18n } from '@shared/i18n';

export default {
  name: 'app-iam-list-table',

  mounted() {
    this.doFetch();
  },

  computed: {
    ...mapGetters({
      rows: 'iam/list/rows',
      count: 'iam/list/count',
      loading: 'iam/list/loading',
      pagination: 'iam/list/pagination',
      isMobile: 'layout/isMobile',
      currentUser: 'auth/currentUser',
      paginationLayout: 'layout/paginationLayout',
      dateFormat: 'auth/manage/dateFormat',
      timeFormat: 'auth/manage/timeFormat',
    }),

    hasPermissionToAuditLogs() {
      return new AuditLogPermissions(this.currentUser).read;
    },

    hasPermissionToEdit() {
      return new IamPermissions(this.currentUser).edit
    },

    fields() {
      const { dateFormat, timeFormat} = this;
      return new UserModel({ dateFormat, timeFormat }).fields();
    }
  },

  methods: {
    ...mapActions({
      doChangeSort: 'iam/list/doChangeSort',
      doChangePaginationCurrentPage:
        'iam/list/doChangePaginationCurrentPage',
      doChangePaginationPageSize:
        'iam/list/doChangePaginationPageSize',
      doFetch: 'iam/list/doFetch',
      // doMountTable: 'iam/list/doMountTable',
    }),

    roleDescriptionOf(roleId) {
      return Roles.descriptionOf(roleId);
    },

    roleLabelOf(roleId) {
      return Roles.labelOf(roleId);
    },

    presenter(row, fieldName) {
      const { dateFormat, timeFormat} = this;
      return new UserModel({ dateFormat, timeFormat }).presenter(row, fieldName);
    },

    onRowClick(row, column) {
      if (!('label' in column)) return;
      // this.doManage(row);
    },

    getIamAvatar(row) {
      if (!('avatars' in row)) return null;
      if (!(row.avatars instanceof Array)) return null;
      if (!row.avatars.length > 0) return null;
      if (!row.avatars[0].publicUrl) return null;
      return `url("${row.avatars[0].publicUrl}")`;
    },

    i18n(code) {
      return i18n(code);
    },
  },
};
</script>

<style>
.app-content-page .iam-list-table .el-table__row {
  cursor: default;
}
</style>
